import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Trash2, User, Wallet, Check, X } from "lucide-react";
import { api, rupiah } from "../lib/dimsum";

const inputCls =
  "w-full rounded-2xl border-2 border-stone-300 bg-stone-50 px-4 py-3 text-base font-semibold text-stone-900 focus:border-rose-500 focus:bg-white focus:outline-none transition-colors";
const rateInput =
  "w-full rounded-xl border-2 border-stone-300 bg-stone-50 px-3 py-2 font-mono-num font-bold focus:border-rose-500 focus:outline-none";
const rateLabel =
  "block mb-1 text-xs font-bold uppercase tracking-wider text-stone-500";

export const Teams = ({ teams, onChanged }) => {
  const [name, setName] = useState("");
  const [editId, setEditId] = useState(null);
  const [edit, setEdit] = useState({ jumbo: 0, mini: 0, bonus: 0, lemburJumbo: 0, lemburMini: 0 });
  const [bulk, setBulk] = useState({ jumbo: "", mini: "", bonus: "", lemburJumbo: "", lemburMini: "" });
  const [bulkSaving, setBulkSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const participants = useMemo(
    () =>
      teams
        .map((t) => ({
          id: t.id,
          name: t.members[0] || t.name,
          members: t.members,
          rate_jumbo: t.rate_jumbo ?? 3250,
          rate_mini: t.rate_mini ?? 6000,
          bonus_harian: t.bonus_harian ?? 0,
          lembur_jumbo: t.lembur_jumbo ?? 0,
          lembur_mini: t.lembur_mini ?? 0,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [teams]
  );

  const makeUnique = (base) => {
    const taken = participants.map((p) => p.name.toLowerCase());
    if (!taken.includes(base.toLowerCase())) return base;
    for (let i = 0; i < 26; i++) {
      const cand = `${base} ${String.fromCharCode(65 + i)}`;
      if (!taken.includes(cand.toLowerCase())) return cand;
    }
    let n = 2;
    while (taken.includes(`${base} ${n}`.toLowerCase())) n++;
    return `${base} ${n}`;
  };

  const add = async () => {
    const base = name.trim();
    if (!base) return toast.error("Isi nama peserta");
    const finalName = makeUnique(base);
    await api.post("/teams", { name: finalName, members: [finalName] });
    if (finalName !== base)
      toast.success(`✅ Nama "${base}" sudah ada, disimpan sebagai "${finalName}"`);
    else toast.success("✅ Peserta ditambahkan");
    setName("");
    onChanged?.();
  };

  const del = async (id) => {
    await api.delete(`/teams/${id}`);
    toast.success("🗑️ Peserta dihapus");
    onChanged?.();
  };

  const startEdit = (p) => {
    setEditId(p.id);
    setEdit({
      jumbo: p.rate_jumbo,
      mini: p.rate_mini,
      bonus: p.bonus_harian,
      lemburJumbo: p.lembur_jumbo,
      lemburMini: p.lembur_mini,
    });
  };

  const saveEdit = async (p) => {
    await api.put(`/teams/${p.id}`, {
      name: p.name,
      members: p.members,
      rate_jumbo: Number(edit.jumbo) || 0,
      rate_mini: Number(edit.mini) || 0,
      bonus_harian: Number(edit.bonus) || 0,
      lembur_jumbo: Number(edit.lemburJumbo) || 0,
      lembur_mini: Number(edit.lemburMini) || 0,
    });
    toast.success("✅ Gaji peserta diperbarui");
    setEditId(null);
    onChanged?.();
  };

  const allSelected = participants.length > 0 && selectedIds.length === participants.length;
  const toggleAll = () =>
    setSelectedIds(allSelected ? [] : participants.map((p) => p.id));
  const toggleOne = (id) =>
    setSelectedIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const applyBulk = async () => {
    const fields = [
      ["jumbo", "rate_jumbo"],
      ["mini", "rate_mini"],
      ["bonus", "bonus_harian"],
      ["lemburJumbo", "lembur_jumbo"],
      ["lemburMini", "lembur_mini"],
    ];
    const patch = {};
    fields.forEach(([k, key]) => {
      if (bulk[k] !== "" && bulk[k] !== null) patch[key] = Number(bulk[k]) || 0;
    });
    if (Object.keys(patch).length === 0)
      return toast.error("Isi minimal satu nilai untuk diterapkan");
    const targets = participants.filter((p) => selectedIds.includes(p.id));
    if (targets.length === 0)
      return toast.error("Pilih minimal satu peserta untuk diterapkan");
    const label = targets.length === participants.length ? "SEMUA" : `${targets.length}`;
    if (!window.confirm(`Terapkan tarif ini ke ${label} peserta (${targets.length})?`)) return;
    setBulkSaving(true);
    try {
      await Promise.all(
        targets.map((p) =>
          api.put(`/teams/${p.id}`, {
            name: p.name,
            members: p.members,
            rate_jumbo: patch.rate_jumbo ?? p.rate_jumbo,
            rate_mini: patch.rate_mini ?? p.rate_mini,
            bonus_harian: patch.bonus_harian ?? p.bonus_harian,
            lembur_jumbo: patch.lembur_jumbo ?? p.lembur_jumbo,
            lembur_mini: patch.lembur_mini ?? p.lembur_mini,
          })
        )
      );
      toast.success(`✅ Gaji ${targets.length} peserta diperbarui`);
      setBulk({ jumbo: "", mini: "", bonus: "", lemburJumbo: "", lemburMini: "" });
      onChanged?.();
    } catch {
      toast.error("Gagal menerapkan (butuh login admin/master)");
    } finally {
      setBulkSaving(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-[340px_1fr] gap-6">
      <div className="bg-white border-2 border-stone-200 rounded-3xl p-6 shadow-sm h-fit space-y-4">
        <h3 className="font-bold text-lg text-stone-900 flex items-center gap-2">
          <UserPlus className="w-5 h-5 text-rose-600" /> Tambah Peserta
        </h3>
        <input
          data-testid="input-new-participant"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Nama peserta (mis. Andi)"
          className={inputCls}
        />
        <button
          data-testid="btn-save-participant"
          onClick={add}
          className="w-full min-h-[48px] rounded-2xl bg-rose-600 hover:bg-rose-700 text-white font-bold active:scale-95 transition-transform"
        >
          Simpan Peserta
        </button>
        <p className="text-sm font-semibold text-stone-500">
          Atur gaji, bonus harian, dan lembur per kg di setiap kartu peserta. Nilai ini dipakai
          pada tab Gaji.
        </p>

        <div className="pt-4 border-t border-stone-200 space-y-2" data-testid="bulk-salary-panel">
          <h3 className="font-bold text-lg text-stone-900 flex items-center gap-2">
            <Wallet className="w-5 h-5 text-emerald-600" /> Atur Gaji Massal
          </h3>
          <p className="text-xs font-semibold text-stone-500">
            Kosongkan kolom yang tidak ingin diubah. Pilih peserta tujuan di bawah (bisa semua atau beberapa).
          </p>
          <div>
            <label className={rateLabel}>Gaji Jumbo (Rp/kg)</label>
            <input data-testid="bulk-jumbo" type="number" value={bulk.jumbo}
              onChange={(e) => setBulk((s) => ({ ...s, jumbo: e.target.value }))} className={rateInput} placeholder="—" />
          </div>
          <div>
            <label className={rateLabel}>Gaji Mini (Rp/kg)</label>
            <input data-testid="bulk-mini" type="number" value={bulk.mini}
              onChange={(e) => setBulk((s) => ({ ...s, mini: e.target.value }))} className={rateInput} placeholder="—" />
          </div>
          <div>
            <label className={rateLabel}>Bonus Harian (Rp/hari)</label>
            <input data-testid="bulk-bonus" type="number" value={bulk.bonus}
              onChange={(e) => setBulk((s) => ({ ...s, bonus: e.target.value }))} className={rateInput} placeholder="—" />
          </div>
          <div>
            <label className={rateLabel}>Lembur Jumbo (Rp/kg)</label>
            <input data-testid="bulk-lembur-jumbo" type="number" value={bulk.lemburJumbo}
              onChange={(e) => setBulk((s) => ({ ...s, lemburJumbo: e.target.value }))} className={rateInput} placeholder="—" />
          </div>
          <div>
            <label className={rateLabel}>Lembur Mini (Rp/kg)</label>
            <input data-testid="bulk-lembur-mini" type="number" value={bulk.lemburMini}
              onChange={(e) => setBulk((s) => ({ ...s, lemburMini: e.target.value }))} className={rateInput} placeholder="—" />
          </div>
          <div className="pt-1" data-testid="bulk-target-picker">
            <div className="flex items-center justify-between mb-1">
              <label className={rateLabel}>Peserta Tujuan</label>
              <button
                type="button"
                data-testid="bulk-toggle-all"
                onClick={toggleAll}
                className="text-xs font-bold text-emerald-700 hover:text-emerald-800"
              >
                {allSelected ? "Kosongkan" : "Pilih Semua"}
              </button>
            </div>
            <div className="max-h-40 overflow-y-auto rounded-xl border-2 border-stone-200 divide-y divide-stone-100">
              {participants.map((p) => (
                <label
                  key={p.id}
                  data-testid={`bulk-target-${p.id}`}
                  className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-stone-50"
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(p.id)}
                    onChange={() => toggleOne(p.id)}
                    className="w-4 h-4 accent-emerald-600"
                  />
                  <span className="text-sm font-semibold text-stone-800 truncate">{p.name}</span>
                </label>
              ))}
              {participants.length === 0 && (
                <p className="px-3 py-2 text-xs font-semibold text-stone-400">Belum ada peserta.</p>
              )}
            </div>
            <p className="mt-1 text-xs font-semibold text-stone-500">{selectedIds.length} peserta dipilih</p>
          </div>
          <button data-testid="btn-apply-bulk" onClick={applyBulk} disabled={bulkSaving}
            className="w-full min-h-[48px] rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold disabled:opacity-50 active:scale-95 transition-transform">
            {bulkSaving
              ? "Menerapkan…"
              : allSelected
              ? "Terapkan ke Semua Peserta"
              : `Terapkan ke ${selectedIds.length} Peserta`}
          </button>
        </div>
      </div>

      <div data-testid="participant-list" className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4 h-fit">
        {participants.map((p) => (
          <div key={p.id} className="bg-white border-2 border-stone-200 rounded-3xl p-4 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="grid place-items-center w-11 h-11 rounded-2xl bg-violet-100 text-violet-700 shrink-0">
                  <User className="w-5 h-5" />
                </div>
                <p className="font-bold text-lg text-stone-900 truncate">{p.name}</p>
              </div>
              <button
                data-testid="btn-delete-participant"
                onClick={() => del(p.id)}
                className="grid place-items-center w-9 h-9 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-600 shrink-0"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>

            {editId === p.id ? (
              <div className="mt-3 space-y-2">
                <div>
                  <label className={rateLabel}>Gaji Jumbo (Rp/kg)</label>
                  <input data-testid={`edit-rate-jumbo-${p.id}`} type="number" value={edit.jumbo}
                    onChange={(e) => setEdit((s) => ({ ...s, jumbo: e.target.value }))} className={rateInput} />
                </div>
                <div>
                  <label className={rateLabel}>Gaji Mini (Rp/kg)</label>
                  <input data-testid={`edit-rate-mini-${p.id}`} type="number" value={edit.mini}
                    onChange={(e) => setEdit((s) => ({ ...s, mini: e.target.value }))} className={rateInput} />
                </div>
                <div>
                  <label className={rateLabel}>Bonus Harian (Rp/hari)</label>
                  <input data-testid={`edit-bonus-${p.id}`} type="number" value={edit.bonus}
                    onChange={(e) => setEdit((s) => ({ ...s, bonus: e.target.value }))} className={rateInput} />
                </div>
                <div>
                  <label className={rateLabel}>Lembur Jumbo (Rp/kg)</label>
                  <input data-testid={`edit-lembur-jumbo-${p.id}`} type="number" value={edit.lemburJumbo}
                    onChange={(e) => setEdit((s) => ({ ...s, lemburJumbo: e.target.value }))} className={rateInput} />
                </div>
                <div>
                  <label className={rateLabel}>Lembur Mini (Rp/kg)</label>
                  <input data-testid={`edit-lembur-mini-${p.id}`} type="number" value={edit.lemburMini}
                    onChange={(e) => setEdit((s) => ({ ...s, lemburMini: e.target.value }))} className={rateInput} />
                </div>
                <div className="flex gap-2 pt-1">
                  <button data-testid={`btn-save-rate-${p.id}`} onClick={() => saveEdit(p)}
                    className="flex-1 min-h-[42px] rounded-xl bg-emerald-600 text-white font-bold flex items-center justify-center gap-1">
                    <Check className="w-4 h-4" /> Simpan
                  </button>
                  <button onClick={() => setEditId(null)}
                    className="min-h-[42px] px-4 rounded-xl bg-stone-100 text-stone-700 font-bold flex items-center justify-center">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-3 flex items-center justify-between gap-2">
                <div className="text-sm font-semibold text-stone-600 leading-tight">
                  <p>Jumbo: <span className="font-mono-num text-stone-900">{rupiah(p.rate_jumbo)}</span>/kg</p>
                  <p>Mini: <span className="font-mono-num text-stone-900">{rupiah(p.rate_mini)}</span>/kg</p>
                  <p>Bonus: <span className="font-mono-num text-stone-900">{rupiah(p.bonus_harian)}</span>/hari</p>
                  <p>Lembur J: <span className="font-mono-num text-stone-900">{rupiah(p.lembur_jumbo)}</span>/kg</p>
                  <p>Lembur M: <span className="font-mono-num text-stone-900">{rupiah(p.lembur_mini)}</span>/kg</p>
                </div>
                <button data-testid={`btn-edit-rate-${p.id}`} onClick={() => startEdit(p)}
                  className="grid place-items-center gap-1 px-3 h-9 rounded-xl bg-violet-50 hover:bg-violet-100 text-violet-700 text-xs font-bold shrink-0">
                  <Wallet className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        ))}
        {participants.length === 0 && (
          <p className="text-stone-400 font-semibold">Belum ada peserta.</p>
        )}
      </div>
    </div>
  );
};
